<template>
    <div class="flex items-center">
        <vx-popover hover placement="top" arrow>
            <button
                type="button"
                class="rounded-full focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                data-test="team-id-badge"
            >
                <vx-badge :color="badgeColor" size="xs">
                    {{ teamId }}
                </vx-badge>
            </button>
            <template v-slot:content>
                <div class="flex flex-col gap-1 max-w-xs text-sm font-normal">
                    <div>
                        <vx-badge color="slate" size="xs">{{ teamId }}</vx-badge>
                    </div>
                    <div class="font-bold text-slate-700 text-sm break-words">{{ teamName }}</div>
                    <div class="text-xs flex items-center gap-1.5">
                        <div>
                            <strong>Status: </strong>
                            <span>{{ formatTeamStatus(teamStatus) }}</span>
                        </div>
                        <span
                            class="inline-flex items-center justify-center h-4 w-4 p-0.5 rounded-full border border-solid"
                            :class="pillClass"
                            aria-hidden="true"
                        >
                            <span class="block w-1.5 h-1.5 rounded-full" :class="pillDotClass"></span>
                        </span>
                    </div>
                    <div v-if="teamStatusReason" class="text-slate-500">
                        {{ teamStatusReason }}
                    </div>
                </div>
            </template>
        </vx-popover>
        <span class="ml-2 truncate text-xs font-bold">{{ teamName }}</span>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { VxBadge, VxPopover } from '@voxie/frontend-components';
import { formatTeamStatus } from '~/utils/team';

type TeamStatus = 'pending' | 'active' | 'suspended' | 'closed';

const TEAM_STATUS_BADGE_COLORS: Record<TeamStatus, 'amber' | 'sky' | 'rose' | 'slate'> = {
    pending: 'amber',
    active: 'sky',
    suspended: 'rose',
    closed: 'slate',
};

const TEAM_STATUS_PILL_CLASSES: Record<TeamStatus, string> = {
    pending: 'bg-amber-100 border-amber-300',
    active: 'bg-sky-100 border-sky-300',
    suspended: 'bg-rose-100 border-rose-300',
    closed: 'bg-slate-100 border-slate-300',
};

const TEAM_STATUS_PILL_DOT_CLASSES: Record<TeamStatus, string> = {
    pending: 'bg-amber-500',
    active: 'bg-sky-500',
    suspended: 'bg-rose-500',
    closed: 'bg-slate-500',
};

const props = defineProps<{
    teamId: number | string;
    teamName: string;
    teamStatus: string;
    teamStatusReason?: string | null;
}>();

const badgeColor = computed(() => TEAM_STATUS_BADGE_COLORS[props.teamStatus as TeamStatus] || 'slate');
const pillClass = computed(
    () => TEAM_STATUS_PILL_CLASSES[props.teamStatus as TeamStatus] || 'bg-slate-100 border-slate-300'
);
const pillDotClass = computed(() => TEAM_STATUS_PILL_DOT_CLASSES[props.teamStatus as TeamStatus] || 'bg-slate-400');
</script>
